import React, { useEffect, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { components } from "../../types/api";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { useGetCampaignByIdQuery } from "../../store/apis/campaignApi";
import {
  useGetMergedCampaignInfoQuery,
  useGetMergedInsertionOrderMetricsQuery,
} from "../../store/apis/internalCampaignsMergeApi";
import InternalCampaignTable from "../table/InternalCampaignTable";
import InsertionOrdersTableWithActions from "../table/InsertionOrdersTableWithActions";

type Campaign = components["schemas"]["CampaignWithRelations"];

interface MetricsCampaignDetailsPanelProps {
  campaign: Campaign;
}

const MetricsCampaignDetailsPanel: React.FC<
  MetricsCampaignDetailsPanelProps
> = ({ campaign }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedInsertionOrderId, setSelectedInsertionOrderId] = useState<
    string | undefined
  >(undefined);

  const campaignId = campaign.id || "";

  const {
    data: campaignData,
    isLoading: isLoadingCampaignDetails,
    error: campaignDetailsError,
  } = useGetCampaignByIdQuery({ campaignId }, { skip: !campaignId });

  const {
    data: mergedInfo,
    isLoading: isLoadingMergedInfo,
    error: mergedInfoError,
  } = useGetMergedCampaignInfoQuery({ campaignId }, { skip: !campaignId });

  const mergedCampaignId =
    mergedInfo && Array.isArray(mergedInfo) && mergedInfo.length > 0
      ? mergedInfo[0].id
      : undefined;

  const {
    data: mergedIOMetrics,
    isLoading: isLoadingInsertionOrders,
    error: insertionOrdersError,
  } = useGetMergedInsertionOrderMetricsQuery(
    { mergedCampaignId: mergedCampaignId as string },
    { skip: !mergedCampaignId, refetchOnFocus: true }
  );

  useEffect(() => {
    if (campaignDetailsError) {
      dispatch(
        setToastMessage({
          message: t(
            "metrics.errors.fetchCampaignDetailsError",
            "Error fetching campaign details"
          ),
          severity: "error",
        })
      );
    }

    if (insertionOrdersError) {
      dispatch(
        setToastMessage({
          message: t(
            "metrics.errors.fetchInsertionOrdersError",
            "Error fetching insertion orders"
          ),
          severity: "error",
        })
      );
    }

    if (mergedInfoError) {
      dispatch(
        setToastMessage({
          message: t(
            "metrics.errors.fetchMergedInfoError",
            "Error fetching campaign merge info"
          ),
          severity: "error",
        })
      );
    }
  }, [
    campaignDetailsError,
    insertionOrdersError,
    mergedInfoError,
    dispatch,
    t,
  ]);

  const sectionTitleSx = {
    fontWeight: 700,
    fontSize: "13px",
    textTransform: "uppercase" as const,
    letterSpacing: "0.04em",
    color: "text.secondary",
    mb: 1,
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box>
        {isLoadingCampaignDetails ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress size={30} />
          </Box>
        ) : campaignData?.data?.internalCampaigns ? (
          <InternalCampaignTable
            internalCampaigns={campaignData.data.internalCampaigns || []}
            maxHeight={380}
            stickyHeader
          />
        ) : (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {t(
                "metrics.internalCampaigns.noData",
                "No internal campaigns available for this campaign"
              )}
            </Typography>
          </Box>
        )}
      </Box>

      {(isLoadingInsertionOrders ||
        isLoadingMergedInfo ||
        (mergedIOMetrics?.insertionOrders?.length ?? 0) > 0) && (
        <Box>
          <Typography sx={sectionTitleSx}>
            {t("metrics.insertionOrders", "Insertion Orders")}
          </Typography>

          <InsertionOrdersTableWithActions
            insertionOrders={mergedIOMetrics?.insertionOrders || []}
            isLoading={isLoadingInsertionOrders || isLoadingMergedInfo}
            maxHeight={380}
            stickyHeader
            selectedInsertionOrderId={selectedInsertionOrderId}
            onInsertionOrderClick={(io) => {
              setSelectedInsertionOrderId(io.id);
              if (io.id) {
                navigate(`/metrics/io-margins/${io.id}`, {
                  state: { insertionOrderName: io.name },
                });
              }
            }}
            currency={mergedIOMetrics?.currency_enum}
          />
        </Box>
      )}
    </Box>
  );
};

export default MetricsCampaignDetailsPanel;
